import { observer } from "mobx-react";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { ChangeEvent, FC, ReactElement, ReactNode, useCallback, useEffect, useState } from "react";
import {
  canDocumentBeReopened,
  DbotAction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionTextModel,
  DetailsPanel,
  detailsPanelToRef,
  DocumentContext,
  documentReopenLabel,
  getDocumentStatus,
  isDocumentSentForSigning,
  isDocumentSigned,
  isDocumentUploadedToSharepoint,
} from "./commonDomain";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { compact, identity, invoke, isNil } from "lodash";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { deferred, when } from "@/utils/functions";
import { Alert } from "@/components-v2/Alert";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { MTextInput, TextInput, TextInputProps } from "@/components-v2/TextInput";
import { Box, VBox } from "@/components-v2/Box";
import { MDateInput, MTimeInput } from "@/components-v2/DateInput";
import { MTextArea } from "@/components-v2/TextArea";
import { MSelect, Select } from "@/components-v2/Select";
import { DropdownMenu, DropdownMenuItemType } from "@/components-v2/Dropdown";
import { Icon } from "@/components-v2/Icon";
import { Tag } from "@/components-v2/Tag";
import { isIconProps } from "@/components-v2/iconHelpers";
import { stopPropagation } from "@/utils/events";
import {
  openSigningStatusDialogAction,
  openViewingStatusDialog,
  unlockAction,
} from "@/dbot/summary/summaryDomain";
import { isMobileView } from "@/utils/screen";
import { Dialog } from "@/components-v2/Dialog";
import { DraggableAttributes } from "@dnd-kit/core";
import { isEnter, isSpace } from "@/utils/keyboardEvents";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { Sortable, SortableList } from "@/components-v2/SortableList";
import { DocumentData } from "@/dbot/project/projectDomain";
import { classNames } from "@/utils/classNames";
import { DocumentLanguage } from "@/common/common";
import { useLanguage, useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { adminBoardArchive, adminShareholderArchive } from "@/utils/navigation";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  LocalDateFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";

import { InfoButton } from "@/components-v2/InfoButton";
import { Markdown } from "@/text/Markdown";
import { DocumentType } from "@/common/documents";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";

type ArticleSelectorProps = {
  model: ArticleSelectorModel;
  name?: string;
  label?: string;
  isLocked?: boolean;
  onChange?: (paragraph: number) => void;
  fullWidth?: boolean;
};

export const ArticleSelector = observer(function ({
  model,
  name,
  isLocked,
  onChange,
  fullWidth,
}: ArticleSelectorProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [isExtraTextEnabled, setIsExtraTextEnabled] = useState(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const checkIfExtraTextShouldBeVisible = useCallback(() => {
    if (ref) {
      const el = ref.querySelector(".article-text");
      setIsExtraTextEnabled(!isNil(el) && el.scrollWidth > el.clientWidth);
    }
  }, [ref]);

  useEffect(() => {
    checkIfExtraTextShouldBeVisible();
  }, [checkIfExtraTextShouldBeVisible]);

  return (
    <VBox ref={setRef}>
      <Select
        fullWidth={fullWidth}
        name={name ?? "select-article"}
        placeholder={t("articles.selectArticle")}
        value={model.selected}
        items={model.options}
        onSelect={(item) => {
          if (onChange) {
            onChange(item);
          } else {
            model.setSelected(item);
          }
          checkIfExtraTextShouldBeVisible();
        }}
        idFn={(item) => String(item)}
        labelFn={(item) => (
          <div>
            <div>{model.getHeading(item, language)}</div>
            <div
              className={
                "article-text text-sm subtle overflow-hidden text-ellipsis whitespace-nowrap max-w-prose"
              }
            >
              {model.getText(item, language)}
            </div>
          </div>
        )}
        disabled={isLocked}
        error={model.error !== undefined}
        message={when(model.error, t)}
      />
      {model.selected && isExtraTextEnabled && (
        <CollapseList openLabel={t("articles.showFullText")} closeLabel={t("articles.hideText")}>
          <div className="text-sm subtle">{model.getText(model.selected, language)}</div>
        </CollapseList>
      )}
    </VBox>
  );
});

export const ArticleSelectorQuestion = observer(
  ({
    question,
    ...rest
  }: {
    question: string;
  } & ArticleSelectorProps) => {
    return (
      <VBox>
        <div>{question}</div>
        <ArticleSelector {...rest} />
      </VBox>
    );
  },
);

export const LocalizedTextQuestion = observer(
  ({
    textModels,
    name,
    question,
    isLocked,
    autoFocus,
  }: {
    textModels: LocalizedStringFieldModel[];
    name: string;
    question: string;
    isLocked: boolean;
    autoFocus?: boolean;
  }) => {
    const { t } = useTranslation();

    if (!textModels.length) {
      return null;
    }

    return (
      <VBox>
        <div>{question}</div>
        {textModels.map((tm, i) => (
          <Box key={tm.locale}>
            <MTextInput
              name={`${name}-${tm.locale}`}
              label={textModels.length > 1 ? t(`language.${tm.locale}`) : undefined}
              model={tm.model}
              readOnly={isLocked}
              autofocus={autoFocus && i === 0}
            />
          </Box>
        ))}
      </VBox>
    );
  },
);
export const LocalizedMarkdownQuestion = observer(
  ({
    textModels,
    name,
    question,
    isLocked,
  }: {
    textModels: LocalizedStringFieldModel[];
    name: string;
    question: string;
    isLocked: boolean;
  }) => {
    const { t } = useTranslation();

    if (!textModels.length) {
      return null;
    }

    return (
      <VBox>
        <div>{question}</div>
        {textModels.map((tm) => (
          <Box key={tm.locale}>
            <MarkdownEditor
              testId={`${name}-${tm.locale}`}
              label={textModels.length > 1 ? t(`language.${tm.locale}`) : undefined}
              value={tm.model.inputValue}
              onChange={tm.model.setAndCommitInputValue}
              readOnly={isLocked}
              size={"full"}
            />
          </Box>
        ))}
      </VBox>
    );
  },
);

export const YesNoQuestion = observer(function YesNoQuestion({
  question,
  infoText,
  field,
  isLocked,
  onChange,
  fieldName,
}: {
  question: string;
  infoText?: ReactElement | TMessage | string;
  fieldName?: string;
  field: BooleanFieldModel;
  isLocked?: boolean;
  onChange?: (flag: boolean) => void;
}) {
  const { t } = useTranslation();
  let changeHandler = (_: ChangeEvent<HTMLInputElement>, flag: boolean) => field.setValue(flag);
  if (onChange) {
    changeHandler = (_: ChangeEvent<HTMLInputElement>, flag: boolean) => onChange(flag);
  }
  return (
    <VBox>
      <div>
        {question}
        &nbsp;
        <InfoButton infoText={infoText} />
      </div>
      <RadioButtonGroup
        name={fieldName ?? question}
        horizontal
        selected={field.value ?? undefined}
        options={[true, false]}
        idFn={identity}
        labelFn={(v) => (v ? t("yes") : t("no"))}
        onChange={(v, option) => changeHandler(v, option)}
        disabled={isLocked}
      />
      {!field.isValid && <Alert type="error" message={<>{when(field.error, t)}</>} />}
    </VBox>
  );
});

export const TextQuestion = observer(function TextQuestion({
  question,
  infoText,
  field,
  isLocked,
  ...props
}: {
  question: string;
  field: StringFieldModel;
  inputLabel?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  infoText?: string;
  isLocked?: boolean;
}) {
  return (
    <VBox>
      <div>
        {question}&nbsp;{infoText && <InfoButton infoText={infoText} />}
      </div>
      <MTextInput {...props} model={field} disabled={isLocked} />
    </VBox>
  );
});

export const TextAreaQuestion = observer(function TextAreaQuestion({
  question,
  field,
  isLocked,
  onChange,
  onBlur,
}: {
  question: string;
  field: StringFieldModel;
  isLocked?: boolean;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onBlur?: () => void;
}) {
  return (
    <VBox>
      <div>{question}</div>
      <MTextArea model={field} onChange={onChange} onBlur={onBlur} disabled={isLocked} />
    </VBox>
  );
});

export const NumberField = observer(function NumberField({
  id,
  name,
  label,
  prefix,
  field,
  disabled,
  inline,
  isLocked,
  onChange,
  onBlur,
}: {
  id?: string;
  name?: string;
  label?: string;
  prefix?: string;
  field: NumberFieldModel;
  disabled?: boolean;
  inline?: boolean;
  isLocked?: boolean;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onBlur?: () => void;
}) {
  const locale = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    field.setLocale(locale);
  }, [locale, field]);

  return (
    <TextInput
      id={id}
      name={name}
      prefix={prefix}
      label={label}
      value={field.inputValue}
      disabled={disabled}
      onChange={onChange ?? field.setInputValue}
      onBlur={() => {
        field.commit();
        if (onBlur) {
          onBlur();
        }
      }}
      onPaste={deferred(field.commit)}
      error={!field.isValid}
      message={when(field.error, t)}
      inline={inline}
      readOnly={isLocked}
    />
  );
});

export const NumberQuestion = observer(function NumberQuestion({
  question,
  infoText,
  ...rest
}: {
  question: string;
  infoText?: string;
  label?: string;
  prefix?: string;
  field: NumberFieldModel;
  isLocked?: boolean;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onBlur?: () => void;
  inline?: boolean;
}) {
  return (
    <VBox>
      {question && (
        <div>
          {question}
          &nbsp;
          <InfoButton infoText={infoText} />
        </div>
      )}
      <NumberField {...rest} />
    </VBox>
  );
});

export const FromToPeriod = observer(function ({
  label,
  from,
  to,
  isLocked,
}: {
  label: string;
  from: LocalDateFieldModel;
  to: LocalDateFieldModel;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>{label}</div>
      <Box gap={"sm"}>
        <MDateInput model={from} label={t("decisions.from")} disabled={isLocked} />
        <MDateInput model={to} label={t("decisions.to")} disabled={isLocked} />
      </Box>
    </VBox>
  );
});

export const DateQuestion = observer(function ({
  question,
  infoText,
  name,
  label,
  field,
  isLocked,
}: {
  question: string;
  infoText?: string;
  name?: string;
  label?: string;
  field: LocalDateFieldModel;
  isLocked?: boolean;
}) {
  return (
    <VBox>
      <div>
        {question}
        &nbsp;
        <InfoButton infoText={infoText} />
      </div>
      <MDateInput model={field} readOnly={isLocked} name={name} label={label} />
    </VBox>
  );
});

export const CheckboxGroupQuestion = observer(function CheckboxGroupQuestion<T>({
  question,
  field,
  isLocked,
}: {
  question: TMessage;
  field: OptionFieldModel<T>;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>{t(question)}</div>
      <MCheckboxGroup model={field} disabled={isLocked} />
    </VBox>
  );
});

export const LocalDateTimePicker = observer(function LocalDateTimePicker({
  dateLabel,
  dateField,
  timeLabel,
  timeField,
  endTimeLabel,
  endTimeField,
  onDateFieldBlur,
  onTimeFieldBlur,
  onEndTimeFieldBlur,
  onDateChange,
  infoText,
  isLocked,
  messageVariant,
}: {
  dateLabel?: TMessage;
  dateField: LocalDateFieldModel;
  timeLabel?: TMessage;
  timeField: TimeFieldModel;
  endTimeLabel?: TMessage;
  endTimeField?: TimeFieldModel;
  onDateFieldBlur?: () => void;
  onTimeFieldBlur?: () => void;
  onEndTimeFieldBlur?: () => void;
  onDateChange?: () => void;
  infoText?: TMessage;
  isLocked?: boolean;
  messageVariant?: TextInputProps["messageVariant"];
}) {
  const { t } = useTranslation();
  return (
    <Box vertical={isMobileView()}>
      <MDateInput
        label={dateLabel !== undefined ? t(dateLabel) : undefined}
        model={dateField}
        onBlur={onDateFieldBlur}
        readOnly={isLocked}
        onChange={(e) => {
          dateField.setAndCommitInputValue(e.input);
          onDateChange?.();
        }}
        messageVariant={messageVariant}
      />

      <Box>
        <MTimeInput
          label={timeLabel !== undefined ? t(timeLabel) : undefined}
          model={timeField}
          onBlur={onTimeFieldBlur}
          readOnly={isLocked}
          messageVariant={messageVariant}
        />
        {endTimeField && (
          <MTimeInput
            label={when(endTimeLabel, t)}
            model={endTimeField}
            onBlur={onEndTimeFieldBlur}
            readOnly={isLocked}
            messageVariant={messageVariant}
          />
        )}
        <InfoButton infoText={when(infoText, t)} />
      </Box>
    </Box>
  );
});

export const DocumentLanguageSelector = observer(function DocumentLanguageSelector({
  field,
  isLocked,
}: {
  field: RadioFieldModel<DocumentLanguage>;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <MSelect
        testId={"documentLanguage"}
        name={"documentLanguage"}
        label={t("dbot.documentLanguage")}
        model={field}
        disabled={isLocked}
      />
    </>
  );
});

export const CollapsibleCheckboxGroup = observer(function CollapsibleCheckboxGroup<T>({
  model,
  bulkSelectLabel,
  disabled,
  labelFn,
  initiallyExpanded,
  onToggleAll,
  onChange,
}: {
  model: OptionFieldModel<T>;
  bulkSelectLabel: TMessage;
  disabled?: boolean;
  labelFn?: (item: T) => string;
  initiallyExpanded?: boolean;
  onToggleAll?: (checked: boolean, toggleCollapse: () => void, isCollapsed: boolean) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>, option: T, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  if (model.options.length === 0) return null;
  if (model.options.length === 1)
    return (
      <MCheckboxGroup model={model} labelFn={labelFn} onChange={onChange} disabled={disabled} />
    );
  return (
    <CollapseList
      closeLabel={t("collapse")}
      openLabel={t("dbot.showAll")}
      initiallyExpanded={initiallyExpanded ?? !model.isAllSelected}
      togglePosition={"start"}
      renderToggle={(toggleCollapse, label, isCollapsed) => (
        <Box justifyContent={"space-between"}>
          <Checkbox
            label={bulkSelectLabel}
            onChange={(event) =>
              onToggleAll
                ? onToggleAll(event.target.checked, toggleCollapse, isCollapsed)
                : model.toggleAll(event.target.checked)
            }
            checked={model.isAllSelected}
            disabled={disabled}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"}>
        <MCheckboxGroup model={model} labelFn={labelFn} onChange={onChange} disabled={disabled} />
      </VBox>
    </CollapseList>
  );
});

export const CollapseList: FC<{
  children?: ReactNode;
  closeLabel: string;
  openLabel: string;
  initiallyExpanded?: boolean;
  togglePosition?: "start" | "end";
  renderToggle?: (toggleCollapse: () => void, label: string, isCollapsed: boolean) => ReactNode;
}> = observer(function CollapseCheckList({
  children,
  closeLabel,
  openLabel,
  initiallyExpanded,
  togglePosition = "end",
  renderToggle,
}) {
  const [collapse, setCollapse] = useState<boolean>(() => !initiallyExpanded);

  const toggleCollapse = () => {
    setCollapse((prevState) => !prevState);
  };

  const label = collapse ? openLabel : closeLabel;
  const toggleNode = renderToggle ? (
    renderToggle(toggleCollapse, label, collapse)
  ) : (
    <Box>
      <LinkButton label={label} onClick={toggleCollapse} />
    </Box>
  );

  return collapse ? (
    toggleNode
  ) : (
    <>
      {togglePosition === "start" && toggleNode}
      {children}
      {togglePosition === "end" && toggleNode}
    </>
  );
});

export const EditDetailsPanel = observer(function EditDetailsPanel({
  dispatch,
  title,
  heading,
  subHeading,
  headingAction,
  content,
  options,
  remove,
  next,
  prev,
  toggleMaximized,
  isMaximized,
}: {
  dispatch: DbotEventDispatcher;
  title?: TMessage;
  heading?: TMessage;
  subHeading?: TMessage | TMessageParam;
  headingAction?: ReactNode;
  remove?: DbotEvent | DbotEvent[];
  next?: DbotEvent;
  prev?: DbotEvent;
  toggleMaximized?: DbotEvent;
  isMaximized?: boolean;
  options?: DbotAction[];
  content: ReactNode | (() => ReactNode);
}) {
  const { t } = useTranslation();

  function render() {
    return (
      <VBox bg={isMaximized ? undefined : "alt1"} overflow={"hidden"} grow>
        <Box
          paddingLeft
          paddingRight={"sm"}
          paddingY={"sm"}
          borderBottom
          alignItems={"center"}
          justifyContent={"space-between"}
          bg={isMaximized ? undefined : "alt1"}
        >
          <div>{when(title, t)}</div>
          <Box alignItems={"center"}>
            <IconButton
              icon={"caret-down"}
              color={"inherit"}
              tooltip={t("next")}
              disabled={!next}
              onClick={() => next && dispatch(next)}
            />
            <IconButton
              icon={"caret-up"}
              color={"inherit"}
              tooltip={t("previous")}
              disabled={!prev}
              onClick={() => prev && dispatch(prev)}
            />
            {toggleMaximized && (
              <IconButton
                icon={isMaximized ? "arrows-in-simple" : "arrows-out-simple"}
                color={"inherit"}
                tooltip={isMaximized ? t("restore") : t("maximize")}
                onClick={() => dispatch(toggleMaximized)}
              />
            )}
            {options || remove ? (
              <DropdownMenu
                button={{ type: "dotdotdot", color: "inherit" }}
                items={[
                  ...(options || []).map<DropdownMenuItemType>((o) => ({
                    type: "item",
                    label: t(o.label),
                    icon: o.icon,
                    onSelect: () => dispatch(o.event),
                  })),
                  remove && {
                    type: "item",
                    label: t("remove"),
                    icon: "trash",
                    color: "error",
                    onSelect: () => dispatch(remove),
                  },
                ]}
              />
            ) : (
              <IconButton className={"invisible"} icon={"x"} tooltip={"placeholder"} disabled />
            )}
            <IconButton
              icon={"x"}
              color={"inherit"}
              tooltip={t("close")}
              onClick={() => dispatch({ type: "dbot/close-details-panel" })}
            />
          </Box>
        </Box>
        <VBox grow overflow={"auto"} padding gap={"md"}>
          {heading && (
            <div>
              <Box justifyContent="space-between" alignItems="center">
                <h4>{t(heading)}</h4>
                {headingAction}
              </Box>
              {subHeading && <div className="caption">{t(subHeading)}</div>}
            </div>
          )}
          {typeof content === "function" ? content() : content}
          {!isMaximized && (
            <>
              <VBox paddingTop={"lg"}>
                <DoneButton onClick={() => dispatch({ type: "dbot/close-details-panel" })} />
              </VBox>
              {remove && <RemoveButton onClick={() => dispatch(remove)} />}
            </>
          )}
        </VBox>
      </VBox>
    );
  }

  if (isMaximized) {
    return (
      <Dialog
        maxWidth={"lg"}
        height={"full"}
        onSubmit={() => dispatch({ type: "dbot/close-details-panel" })}
        submitLabel={t("done")}
      >
        {render()}
      </Dialog>
    );
  }
  return render();
});

export const DetailsSummary = observer(function DetailsSummary({
  panel,
  onOpen,
  isDragDisabled,
  dispatch,
  withoutBg,
  isDragging,
  dragAttributes,
  dragListeners,
}: {
  panel: DetailsPanel;
  onOpen: (panel: DetailsPanel) => void;
  isDragDisabled?: boolean;
  dispatch: DbotEventDispatcher;
  withoutBg?: boolean;
  isDragging?: boolean;
  dragAttributes: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
}) {
  const { t } = useTranslation();
  const iconProps = isIconProps(panel.icon) ? panel.icon : { name: panel.icon };
  const action = panel.action;
  return (
    <Box
      bg={withoutBg ? undefined : "alt1"}
      gap={false}
      rounded
      alignItems={"stretch"}
      className={classNames(
        "draggable hover:bg-alt2 group",
        isDragging ? "cursor-grabbing" : "cursor-pointer",
      )}
      tooltip={!isDragDisabled ? t("details-summary.tooltip") : undefined}
      dropShadow={isDragging ? "lg" : undefined}
      htmlProps={{
        onClick: () => onOpen(panel),
        ...dragAttributes,
        ...dragListeners,
        onKeyDown: (event) => {
          const target = event.target as Element;
          if (target.classList.contains("drag-handle")) {
            invoke(dragListeners, "onKeyDown", event);
          } else if (target.classList.contains("draggable") && (isEnter(event) || isSpace(event))) {
            event.preventDefault();
            onOpen(panel);
          }
        },
      }}
    >
      {isDragDisabled ? (
        <Box paddingX alignItems={"center"}>
          <Icon {...iconProps} />
        </Box>
      ) : (
        <Box
          paddingX
          alignItems={"center"}
          className={classNames(
            isDragging ? "cursor-grabbing" : "cursor-grab",
            "group/handle",
            "drag-handle",
          )}
          htmlProps={{
            ...dragAttributes,
            ...dragListeners,
          }}
        >
          <Icon
            name={"dots-six-vertical"}
            weight={"bold"}
            className={"hidden group-hover:block group-focus/handle:block"}
          />
          <Icon {...iconProps} className={"block group-hover:hidden group-focus/handle:hidden"} />
        </Box>
      )}
      <Box
        paddingY
        paddingRight
        grow
        alignItems={"center"}
        className={"select-none"}
        overflow="hidden"
      >
        {when(panel.summaryHeading ?? panel.heading, (text) => (
          <Markdown value={t(text)} preview truncate />
        ))}
        {panel.hasValidationError && (
          <Icon name={"warning"} color={"error"} className={"align-text-bottom"} />
        )}
        {!panel.hasValidationError && panel.tag && <Tag color={"info"} label={t(panel.tag)} />}
        <Box display="block" marginLeft={"auto"} className={"subtle whitespace-nowrap"}>
          {action ? (
            <LinkButton
              icon={action.icon}
              label={t(action.label)}
              onClick={stopPropagation(() => dispatch(action.event))}
              size={"sm"}
              weight={"normal"}
            />
          ) : (
            when(panel.label, t)
          )}
        </Box>
        {panel.infoMessage && <InfoButton infoText={<Markdown value={t(panel.infoMessage)} />} />}
      </Box>
    </Box>
  );
});

export const DetailsSummaryList = observer(function DetailsSummaryList({
  dispatch,
  isLocked,
  panels,
  onMove,
  ...props
}: {
  dispatch: DbotEventDispatcher;
  isLocked: boolean;
  panels: DetailsPanel[];
  onMove?: (panel: DetailsPanel, toPanel: DetailsPanel) => void;
  canMove?: (panel: DetailsPanel) => boolean;
}) {
  const handleOpen = (panel: DetailsPanel) =>
    panel.isDisabled
      ? undefined
      : dispatch(
          panel.customOpenEvent ?? {
            type: "dbot/open-details-panel",
            panel: detailsPanelToRef(panel),
          },
        );

  const handleMove =
    onMove ??
    ((panel: DetailsPanel, toPanel: DetailsPanel) =>
      dispatch({
        type: "dbot/move-decision",
        decisionId: panel.id,
        toDecisionId: toPanel.id,
      }));

  const canMove = props.canMove ?? ((panel: DetailsPanel) => panel.type === "decision");

  if (!panels.length) {
    return null;
  }

  return (
    <SortableList items={panels} onMove={handleMove}>
      <VBox gap={"xs"} testId={"matters"}>
        {panels.map((panel) => (
          <Sortable
            key={panel.id}
            id={panel.id}
            isDragDisabled={isLocked || panel.isDisabled || !canMove(panel)}
          >
            {(props) => (
              <DetailsSummary
                key={panel.id}
                panel={panel}
                onOpen={handleOpen}
                dispatch={dispatch}
                withoutBg={isLocked || panel.notReady || panel.isDisabled}
                {...props}
              />
            )}
          </Sortable>
        ))}
      </VBox>
    </SortableList>
  );
});

export function DoneButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return <Button variant={"secondary"} icon={"check"} label={t("done")} onClick={onClick} />;
}

export function RemoveButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return <LinkButton color={"error"} icon={"trash"} label={t("remove")} onClick={onClick} />;
}

export function ReopenDocumentButton({
  context,
  dispatch,
}: {
  context: DocumentContext<DocumentData>;
  dispatch: DbotEventDispatcher;
}) {
  const { t } = useTranslation();
  if (!canDocumentBeReopened(context, context.documentId)) {
    return null;
  }
  return (
    <Button
      variant={"secondary"}
      label={t(documentReopenLabel(context, context.documentId))}
      onClick={() => dispatch(unlockAction(context.documentId))}
    />
  );
}

export const DocumentSigningStatusLine = observer(
  ({
    context,
    dispatch,
  }: {
    context: DocumentContext<DocumentData>;
    dispatch: DbotEventDispatcher;
  }) => {
    const { t } = useTranslation();
    const { formatDateTime } = useLocaleFormat();
    const documentStatus = getDocumentStatus(context, context.documentId);
    const documentItem = context.currentDocuments.find((d) => d.documentId === context.documentId)!;

    if (isDocumentSigned(context, context.documentId)) {
      const isUploadedToSharepoint = isDocumentUploadedToSharepoint(context, context.documentId);
      return (
        <VBox gap={false}>
          <Box alignItems={"center"} gap={"md"} padding>
            <Icon name={"signature"} />
            <span>
              {t("signing-complete")} {formatDateTime(documentStatus?.signedAt)}
            </span>
          </Box>

          {context.documentData.type === DocumentType.general_meeting && (
            <Box alignItems={"center"} gap={"md"} padding>
              <Icon name={"file-lock"} />
              <Markdown
                value={t(
                  isUploadedToSharepoint
                    ? "dbot.sealed-copy-to-shareholder-archive-and-sharepoint"
                    : "dbot.sealed-copy-to-shareholder-archive",
                  {
                    link: adminShareholderArchive(context.orgNo),
                  },
                )}
                enableLinks
                disableLastMargin
              />
            </Box>
          )}
          {context.documentData.type === DocumentType.board_meeting && (
            <Box alignItems={"center"} gap={"md"} padding>
              <Icon name={"file-lock"} />
              <Markdown
                value={t(
                  isUploadedToSharepoint
                    ? "dbot.sealed-copy-to-board-archive-and-sharepoint"
                    : "dbot.sealed-copy-to-board-archive",
                  {
                    link: adminBoardArchive(context.orgNo),
                  },
                )}
                enableLinks
              />
            </Box>
          )}
        </VBox>
      );
    } else if (isDocumentSentForSigning(context, context.documentId)) {
      return (
        <Box alignItems={"center"} justifyContent={"space-between"} padding>
          <Box alignItems={"center"}>
            <Icon name={"pen"} /> {t("sent")} {formatDateTime(documentStatus?.sentAt)}
          </Box>
          {when(openSigningStatusDialogAction(documentItem, documentStatus), (action) => (
            <LinkButton label={action.label} onClick={() => dispatch([action])} />
          ))}
        </Box>
      );
    }
    return null;
  },
);

export const DocumentViewingStatusLine = observer(
  ({
    context,
    dispatch,
  }: {
    context: DocumentContext<DocumentData>;
    dispatch: DbotEventDispatcher;
  }) => {
    const { t } = useTranslation();
    const { formatDateTime } = useLocaleFormat();
    const documentStatus = getDocumentStatus(context, context.documentId);
    const documentItem = context.currentDocuments.find((d) => d.documentId === context.documentId)!;
    if (documentStatus?.sentAt) {
      return (
        <>
          <Box alignItems={"center"} justifyContent={"space-between"} padding>
            <Box alignItems={"center"}>
              <Icon name={"paper-plane"} /> {t("sent")} {formatDateTime(documentStatus.sentAt)}
            </Box>
            {when(openViewingStatusDialog(documentItem, documentStatus), (action) => (
              <LinkButton label={action.label} onClick={() => dispatch(action)} />
            ))}
          </Box>
        </>
      );
    }
    return null;
  },
);

export const NoticeDocumentAuthStatusLine = observer(
  ({ context }: { context: DocumentContext<DocumentData> }) => {
    const { t } = useTranslation();
    const documentStatus = getDocumentStatus(context, context.documentId);
    if (documentStatus?.meetingPassword) {
      return (
        <>
          <Box alignItems={"center"} justifyContent={"space-between"} padding>
            <Box alignItems={"center"}>
              <Icon name={"lock"} /> {t("meeting-password")}
            </Box>
            <Box alignItems={"center"}>{documentStatus.meetingPassword}</Box>
          </Box>
        </>
      );
    }
    return null;
  },
);

export const DecisionTextsView = observer(
  ({
    form,
    hideIntro = false,
    isLocked,
  }: {
    form: DecisionTextModel;
    hideIntro?: boolean;
    isLocked: boolean;
  }) => {
    const { t } = useTranslation();
    const addButtonConfigs: { model: BooleanFieldModel; label: TMessage }[] = compact([
      !hideIntro && { model: form.showIntro, label: { key: "dbot.add-intro" } },
      { model: form.showDescription, label: { key: "dbot.add-description" } },
    ]);
    const addButtons = addButtonConfigs
      .filter((config) => !config.model.value)
      .map((config) => (
        <Button
          key={t(config.label)}
          variant={"basic"}
          size={"sm"}
          icon={"plus"}
          label={config.label}
          onClick={config.model.toggle}
          disabled={isLocked}
        />
      ));

    return (
      <VBox marginY={"md"} gap={"md"}>
        {addButtons.length > 0 && <Box marginY={"md"}>{addButtons}</Box>}
        {form.showIntro.value && <LocalizedTextEditor model={form.introTexts} />}
        {form.showDescription.value && <LocalizedTextEditor model={form.model} />}
      </VBox>
    );
  },
);
